import { Lightbulb, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  H2,
  P,
  Pull,
  Callout,
  ExampleTable,
  RolePair,
  RulesGrid,
  BigRule,
  Li,
} from "./_blocks";

export default function FairTradingProfit() {
  return (
    <>
      <Pull>
        <strong>Quick answer:</strong> fair trading isn't a compliance tax — it's a profit
        lever. Honest pricing builds trust, and trust is the single most reliable predictor of
        repeat revenue. The businesses that take this seriously charge more, not less.
      </Pull>

      <H2>If you remember one line, remember this</H2>
      <P>
        New Zealand business owners often think of the Fair Trading Act as a hoop to jump
        through — something the legal team checks once a year so the Commerce Commission stays
        away. That mindset leaves money on the table.
      </P>

      <Callout tone="ink" icon={Lightbulb} kicker="The big idea">
        <p className="m-0">
          Fair trading isn't about being nice. It's about{" "}
          <strong>building a business customers don't leave.</strong> Every misleading price
          tag, hidden fee, and "was/now" sticker that wasn't really discounted is a withdrawal
          from your most valuable account: the trust your customers extend to you.
        </p>
      </Callout>

      <H2>Why this matters financially (the part nobody explains)</H2>
      <P>
        Five concrete ways trading fairly puts more money in the bank — usually within two
        quarters of changing the way you price and quote:
      </P>

      <RolePair
        left={{
          accent: "green",
          kicker: "01 · Repeat revenue",
          title: "Trust → loyalty → recurring sales",
          lines: [
            "Customers come back when prices are clear and consistent.",
            "Word-of-mouth referrals lower your acquisition cost.",
            "Lifetime value rises faster than the cost of being honest.",
          ],
          verdict: "The cheapest customer is the one you already have.",
        }}
        right={{
          accent: "gold",
          kicker: "02 · Pricing power",
          title: "Less price-shopping, more value-buying",
          lines: [
            "Trusted brands get less aggressive haggling.",
            "Customers who understand the price defend it.",
            "You can hold margin in a competitive market.",
          ],
          verdict: "Honesty earns you the right to charge full price.",
        }}
      />

      <RolePair
        left={{
          accent: "ink",
          kicker: "03 · Brand equity",
          title: "Slow to build, hard to lose",
          lines: [
            "Reputation compounds quietly month after month.",
            "Reviews and testimonials become free marketing.",
            "Strong reviews lift conversion on every other channel.",
          ],
          verdict: "Brand is the lagging indicator of how you trade.",
        }}
        right={{
          accent: "red",
          kicker: "04 · Risk you don't see coming",
          title: "Fines, refunds, chargebacks",
          lines: [
            "NZ Fair Trading Act fines run into the millions.",
            "Disputed transactions trigger Stripe / bank chargebacks.",
            "Refund liabilities sit on your balance sheet long-term.",
          ],
          verdict: "One viral complaint outruns a year of marketing.",
        }}
      />

      <H2>The maths nobody runs</H2>
      <P>
        Most business owners measure margin per sale. Fair-trading-aware owners measure margin
        per <em>customer relationship</em>. Same product, two completely different numbers:
      </P>

      <ExampleTable
        rows={[
          ["One-off sale (transactional pricing)", "$1,200"],
          ["Trust-built relationship · 5 repeats over 3 years", "$6,000"],
          ["Referrals from that customer (typical: 2)", "+$2,400"],
          ["True lifetime value of one fairly-traded customer", "$8,400"],
        ]}
        footnote="Even at the same headline price, fair trading multiplies revenue 7x by turning each customer into a multi-year asset. The hidden 15% discount you 'saved' by misleading them on the first sale costs you the next $7,200."
      />

      <Callout tone="warning" icon={AlertTriangle} kicker="The Fair Trading Act, briefly">
        <p className="m-0 mb-2">
          The NZ Fair Trading Act bans:
        </p>
        <ul className="m-0 pl-5 space-y-1">
          <li>Misleading prices (fake "was/now" discounts that weren't really discounted).</li>
          <li>Hidden fees and surcharges not disclosed before checkout.</li>
          <li>False claims about a product, service, or limited-time offer.</li>
          <li>Pressure tactics that obscure the true cost of buying.</li>
        </ul>
        <p className="m-0 mt-3 text-sm text-slate-600">
          Penalties run up to <strong>$600,000 per breach for companies</strong>{" "}
          ($200,000 for individuals). Most breaches aren't intentional — they're sloppy
          systems. Which is exactly where good bookkeeping fixes the problem at the source.
        </p>
      </Callout>

      <H2>The big rule (write this on the wall)</H2>
      <P>
        Fair trading is a positioning decision before it's a compliance decision. Once you
        decide which side you're on, the rest of the business gets easier:
      </P>

      <BigRule
        green={{ label: "Trade fairly", value: "Customers pay you for years" }}
        red={{ label: "Trade unfairly", value: "Customers pay you once" }}
      />

      <H2>Where most NZ businesses slip up (without realising)</H2>
      <P>
        We see the same five system errors over and over. None of them are deliberate — they're
        just what happens when pricing, quoting, and invoicing aren't joined up:
      </P>
      <ul className="list-none p-0 space-y-3 my-6">
        <Li tone="bad">
          Quotes that say <em>"$X total"</em> with GST hidden inside, then invoices that show
          GST separately. The customer sees two different numbers and feels misled.
        </Li>
        <Li tone="bad">
          Discount stickers (<em>"was $99, now $59"</em>) where the original price was the
          sale price for months. That's a Fair Trading breach, even if you didn't mean it.
        </Li>
        <Li tone="bad">
          Surcharges (card fees, weekend rates, after-hours premiums) added at checkout
          without being disclosed up front. Stripe loves chargebacks on these.
        </Li>
        <Li tone="bad">
          Service descriptions on the website that don't match what's actually delivered.
          Common with package-based pricing where features quietly drift.
        </Li>
        <Li tone="bad">
          Product photos that don't reflect the actual stock you're selling. Especially
          painful for retail and second-hand goods.
        </Li>
      </ul>

      <H2>The 3 rules to convert fair trading into profit</H2>
      <RulesGrid
        rules={[
          {
            n: "01",
            title: "One number, everywhere",
            body: "Quote, invoice, website, and receipt all show the same total. If GST is on the price tag, it's on the invoice. If a delivery fee exists, it's on the quote. No surprises means no friction.",
          },
          {
            n: "02",
            title: "Show your maths",
            body: "When customers see the breakdown — labour, materials, GST, fees — they stop comparing on price and start comparing on value. Itemised quotes outsell vague quotes 2-to-1.",
          },
          {
            n: "03",
            title: "Discount honestly or not at all",
            body: 'Real discounts only. If you say "was $99 now $59", the $99 must have been the genuine recent price. If it wasn\'t — drop the sticker, hold the higher price, and let the value speak.',
          },
        ]}
      />

      <Callout tone="success" icon={CheckCircle2} kicker="The accountant's one-liner">
        <p className="m-0">
          "Fair trading isn't about being nice — it's about building a business customers
          don't leave. The numbers show up in repeat revenue, not in the legal column."
        </p>
      </Callout>

      <H2>How this connects to your books</H2>
      <P>
        Most Fair Trading slip-ups aren't ethical failures — they're <strong>system failures</strong>.
        Fix the systems and the compliance and the trust both look after themselves. Here's
        what we put in place for clients on our monthly subscription:
      </P>
      <ul className="list-none p-0 space-y-3 my-6">
        <Li tone="good">
          Set up your Xero so every invoice template shows the same clear pricing structure
          your quotes use — <strong>no silent reformatting between quote and invoice</strong>.
        </Li>
        <Li tone="good">
          Lock in a <strong>GST-exclusive vs GST-inclusive convention</strong> for every
          product/service, then enforce it in your invoicing tool so staff can't accidentally
          flip it.
        </Li>
        <Li tone="good">
          Email you a <strong>monthly review</strong> flagging unusual discount patterns,
          pricing inconsistencies between channels, or surcharges that aren't on the public
          price list.
        </Li>
      </ul>

      <P>
        It's small, repeatable, and it pays for itself in two ways at once: fewer disputes and
        more repeat revenue. The cleanest way to comply with the Fair Trading Act is also the
        cleanest way to grow.
      </P>
    </>
  );
}
